/**
 * Sélecteur de langue FR/EN.
 * Visible des visiteurs anonymes (header) et dans les paramètres de compte.
 * La bascule ne recharge pas la page (re-render i18next natif) → l'état des
 * formulaires en cours survit. Persistance via localStorage (clé i18next).
 */
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { type AppLocale, SUPPORTED_LOCALES, setLocale } from "../i18n";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const LABELS: Record<AppLocale, string> = { fr: "FR", en: "EN", es: "ES" };
const NAMES: Record<AppLocale, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
};

export interface LanguageSwitcherProps {
  className?: string;
  /** "compact" = boutons FR|EN ; "icon" = icône + langue courante cliquable */
  variant?: "compact" | "icon";
}

export function LanguageSwitcher({
  className,
  variant = "compact",
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation("common");
  const { user, updateProfile } = useAuth();
  const rawLng = i18n.language?.slice(0, 2) || "fr";
  // Sécurité : forcer un AppLocale valide. Si le code locale est corrompu
  // ou invalide, fallback à "fr". Cela prévient les états incohérents
  // (ex : bug "EST" où le code devient mal-formé).
  const current: AppLocale = SUPPORTED_LOCALES.includes(rawLng as AppLocale)
    ? (rawLng as AppLocale)
    : "fr";

  // Log pour debug le bug "EST" : tracer quand la locale s'écarte de SUPPORTED_LOCALES
  if (typeof window !== "undefined" && rawLng !== current) {
    console.warn(
      `[LanguageSwitcher] Invalid locale detected: "${rawLng}" → fallback to "${current}". i18n.language="${i18n.language}".`,
    );
  }

  // Bascule la langue (re-render i18next, pas de reload) + persiste en base
  // pour les utilisateurs connectés (sinon localStorage suffit).
  const changeLocale = (loc: AppLocale) => {
    console.log(
      `[LanguageSwitcher] Changing locale from "${current}" to "${loc}"`,
    );
    void setLocale(loc);
    if (user) void updateProfile({ preferred_language: loc });
  };

  if (variant === "icon") {
    // Menu déroulant : liste FR/EN/ES avec coche sur la langue active.
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t("change_language", { current: LABELS[current] })}
          translate="no"
          className={cn(
            "notranslate",
            "inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded",
            className,
          )}
        >
          <Globe className="w-4 h-4" />
          {LABELS[current]}
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[9rem]">
          {SUPPORTED_LOCALES.map((loc) => (
            <DropdownMenuItem
              key={loc}
              onSelect={() => changeLocale(loc)}
              className="flex items-center justify-between gap-2 cursor-pointer"
            >
              <span>{NAMES[loc]}</span>
              {current === loc && <Check className="w-4 h-4 text-indigo-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div
      role="group"
      aria-label="Language"
      className={cn(
        "inline-flex items-center rounded-md border border-slate-600/50 overflow-hidden",
        className,
      )}
    >
      {SUPPORTED_LOCALES.map((loc) => (
        <button
          key={loc}
          type="button"
          onClick={() => changeLocale(loc)}
          aria-pressed={current === loc}
          className={cn(
            "px-2.5 py-1 text-xs font-semibold transition-colors",
            current === loc
              ? "bg-indigo-600 text-white"
              : "text-gray-400 hover:text-white",
          )}
        >
          {LABELS[loc]}
        </button>
      ))}
    </div>
  );
}

export default LanguageSwitcher;
